import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Col, Row } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import FormDate from '../../../../antd/form/FormDate/FormDate';
import FormTextEditor from '../../../../antd/form/FormTextEditor/FormTextEditor';
import FormFileUpload from '../../../../antd/form/FormFileUpload/FormFileUpload';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField';
import FormButton from '../../../../antd/form/FormButton/FormButton';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer';
import { required } from '../../../../antd/form/FormValidation';
import {
  useDefendPhdThesisMutation,
  useGetPhdStudentQuery,
  useGetPhdThesisQuery,
  useLazyGetPhdThesisFileQuery,
} from '../../../../../redux/services/phd';
import { downloadBlobResponse } from '../../../../../utils/helpers/downloadBlobResponse';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import PhdThesesDefendModal from './PhdThesesDefendModal.jsx';

const PhdThesesDefend = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = AntdForm.useForm();
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [previewValues, setPreviewValues] = useState({});
  const pageTitle = t('Označiť tému ako obhájenú');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { data: detail, isLoading } = useGetPhdThesisQuery(id);
  const { data: studentDetail, isLoading: isStudentLoading } =
    useGetPhdStudentQuery(detail?.phd_student_id, {
      skip: !detail?.phd_student_id,
    });
  const [defendThesis, { isLoading: isDefending }] =
    useDefendPhdThesisMutation();
  const [fetchThesisFile] = useLazyGetPhdThesisFileQuery();
  const { saveSuccess, saveFailed } = useToastMessages();
  const { studyFieldMap, isLoading: isReferenceLoading } = usePhdReferenceData({
    includeStudents: false,
    includeDepartments: false,
    includeUsers: false,
    includeExaminers: false,
  });

  const studyFieldLabel = useMemo(() => {
    if (!detail) {
      return null;
    }
    return (
      detail.legacy_study_program_name ||
      studyFieldMap.get(detail.phd_study_field_id) ||
      detail?.studyField?.study_field ||
      null
    );
  }, [detail, studyFieldMap]);

  const studentStudyProgramLabel = useMemo(() => {
    if (!studentDetail) {
      return null;
    }

    return (
      studentDetail?.study_field?.study_field ||
      studentDetail?.studyField?.study_field ||
      studyFieldMap.get(studentDetail?.phd_study_field_id) ||
      null
    );
  }, [studentDetail, studyFieldMap]);

  const initialValues = useMemo(() => {
    if (!detail) {
      return null;
    }

    return {
      defended_at: dayjs(),
      reviewer1: detail.reviewer1 || '',
      reviewer2: detail.reviewer2 || '',
      reviewer3: detail.reviewer3 || '',
      abstract_sk: detail.abstract_sk || '',
      abstract_en: detail.abstract_en || '',
      summary_file: null,
      fulltext_file: null,
    };
  }, [detail]);

  const handleDownloadExistingFile = async (type) => {
    if (!detail?.id) {
      return;
    }
    try {
      const response = await fetchThesisFile({ id: detail.id, type }).unwrap();
      downloadBlobResponse(response);
    } catch (error) {
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  const resolvedLegacyStudyProgramName =
    studentStudyProgramLabel ||
    studyFieldLabel ||
    detail?.legacy_study_program_name ||
    null;
  const resolvedStudentDisplayName =
    detail?.student_display_name ||
    detail?.student?.person?.full_name ||
    [detail?.student?.person?.first_name, detail?.student?.person?.last_name]
      .filter(Boolean)
      .join(' ') ||
    null;
  const resolvedSupervisorDisplayName =
    detail?.supervisor_display_name ||
    detail?.supervisor?.full_name ||
    detail?.supervisor?.display_name ||
    [detail?.supervisor?.first_name, detail?.supervisor?.last_name]
      .filter(Boolean)
      .join(' ') ||
    null;

  const isEligible = Boolean(
    detail?.approved_at && !detail?.archived_at && detail?.phd_student_id,
  );

  const handlePreview = () => {
    const values = form.getFieldsValue(true);
    setPreviewValues({
      ...values,
      title_sk: detail?.title_sk,
      student_display_name: resolvedStudentDisplayName || '',
      supervisor_display_name: resolvedSupervisorDisplayName || '',
      legacy_study_program_name: resolvedLegacyStudyProgramName || '',
    });
    setIsPreviewVisible(true);
  };

  const handleSubmit = async (values) => {
    if (!detail?.id) {
      return;
    }

    const payload = new FormData();
    payload.append(
      'defended_at',
      values.defended_at ? dayjs(values.defended_at).toISOString() : '',
    );
    payload.append('student_display_name', resolvedStudentDisplayName || '');
    payload.append(
      'supervisor_display_name',
      resolvedSupervisorDisplayName || '',
    );
    payload.append(
      'legacy_study_program_name',
      resolvedLegacyStudyProgramName || '',
    );
    payload.append('reviewer1', values.reviewer1?.trim() || '');
    payload.append('reviewer2', values.reviewer2?.trim() || '');
    payload.append('reviewer3', values.reviewer3?.trim() || '');
    payload.append('abstract_sk', values.abstract_sk?.trim() || '');
    payload.append('abstract_en', values.abstract_en?.trim() || '');
    if (values.summary_file instanceof File) {
      payload.append('summary_file', values.summary_file);
    }
    if (values.fulltext_file instanceof File) {
      payload.append('fulltext_file', values.fulltext_file);
    }

    try {
      await defendThesis({ id: detail.id, body: payload }).unwrap();
      saveSuccess();
      navigate(routes.cmsPhdTheses);
    } catch (error) {
      saveFailed(error);
    }
  };

  if (
    isLoading ||
    isReferenceLoading ||
    (detail?.phd_student_id && isStudentLoading) ||
    !initialValues
  ) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdTheses}
        backUrlTooltip={t('Témy dizertačných prác')}
        permissionsBasic="phd-theses"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako zaevidovať obhajobu témy?')}
          description={t(
            'Administrátor doktorandského štúdia doplní verejné údaje obhajoby a súbory schválenej účinnej témy priradenej doktorandovi. Uložením vznikne obhájený, archivovaný a verejný záznam.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-graduation-cap"
          link={routes.cmsPhdTheses}
          title={pageTitle}
        >
          {!isEligible && (
            <Alert
              type="warning"
              showIcon
              message={t(
                'Tému je možné označiť ako obhájenú len ak je schválená, nearchivovaná a má priradeného študenta.',
              )}
              style={{ marginBottom: 16 }}
            />
          )}
          <Form
            form={form}
            initialValues={initialValues}
            onFinish={handleSubmit}
          >
            <Row gutter={16}>
              <Col span={24}>
                <FormDate
                  label={t('Dátum obhajoby')}
                  name="defended_at"
                  rules={[required(t)]}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <FormTextField label={t('Oponent 1')} name="reviewer1" />
              </Col>
              <Col span={8}>
                <FormTextField label={t('Oponent 2')} name="reviewer2" />
              </Col>
              <Col span={8}>
                <FormTextField label={t('Oponent 3')} name="reviewer3" />
              </Col>
            </Row>
            <FormTextEditor
              label={t('Abstrakt (slovensky)')}
              name="abstract_sk"
              imgUploadDirectory="dissertations"
              fileUploadDirectory="dissertations"
            />
            <FormTextEditor
              label={t('Abstrakt (anglicky)')}
              name="abstract_en"
              imgUploadDirectory="dissertations"
              fileUploadDirectory="dissertations"
            />
            <Row gutter={16}>
              <Col span={12}>
                <FormFileUpload
                  label={t('Autoreferát')}
                  name="summary_file"
                  fileName={detail?.summary_file}
                />
                {detail?.summary_file && (
                  <a
                    onClick={() => handleDownloadExistingFile('summary')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('Stiahnuť aktuálny súbor')}
                  </a>
                )}
              </Col>
              <Col span={12}>
                <FormFileUpload
                  label={t('Text práce')}
                  name="fulltext_file"
                  fileName={detail?.fulltext_file}
                />
                {detail?.fulltext_file && (
                  <a
                    onClick={() => handleDownloadExistingFile('fulltext')}
                    style={{ cursor: 'pointer' }}
                  >
                    {t('Stiahnuť aktuálny súbor')}
                  </a>
                )}
              </Col>
            </Row>
            <FormButtonsContainer>
              <FormButton submitOnClick={false} onClick={handlePreview}>
                {t('Náhľad')}
              </FormButton>
              <FormButton
                submitOnClick
                submitKey="phd-theses"
                disabled={!isEligible || isDefending}
              >
                {t('Označiť ako obhájenú')}
              </FormButton>
            </FormButtonsContainer>
          </Form>
          <PhdThesesDefendModal
            visible={isPreviewVisible}
            onCancel={() => setIsPreviewVisible(false)}
            values={previewValues}
          />
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdThesesDefend;
